'use client';

import { ArrowRight, Megaphone, Sparkles, TriangleAlert, Wrench, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  isWithinCampaignWindow,
  supportsCampaignPlacement,
} from '@/config/site-campaigns';

const TONE_STYLES = {
  promo: {
    shell: 'border-b border-amber-400/20 bg-[linear-gradient(90deg,rgba(54,38,18,0.98),rgba(99,67,18,0.98),rgba(54,38,18,0.98))] text-amber-50',
    label: 'bg-amber-400/18 text-amber-200 ring-1 ring-inset ring-amber-300/25',
    accent: 'text-amber-300',
    button: 'bg-white text-neutral-950 hover:bg-amber-100',
    icon: Sparkles,
  },
  info: {
    shell: 'border-b border-sky-400/20 bg-[linear-gradient(90deg,rgba(11,25,36,0.98),rgba(17,55,84,0.98),rgba(11,25,36,0.98))] text-sky-50',
    label: 'bg-sky-400/18 text-sky-200 ring-1 ring-inset ring-sky-300/25',
    accent: 'text-sky-300',
    button: 'bg-white text-sky-950 hover:bg-sky-100',
    icon: Megaphone,
  },
  maintenance: {
    shell: 'border-b border-orange-400/20 bg-[linear-gradient(90deg,rgba(48,26,14,0.98),rgba(92,53,17,0.98),rgba(48,26,14,0.98))] text-orange-50',
    label: 'bg-orange-400/18 text-orange-200 ring-1 ring-inset ring-orange-300/25',
    accent: 'text-orange-300',
    button: 'bg-white text-orange-950 hover:bg-orange-100',
    icon: Wrench,
  },
  warning: {
    shell: 'border-b border-rose-400/20 bg-[linear-gradient(90deg,rgba(44,16,22,0.98),rgba(92,24,41,0.98),rgba(44,16,22,0.98))] text-rose-50',
    label: 'bg-rose-400/18 text-rose-200 ring-1 ring-inset ring-rose-300/25',
    accent: 'text-rose-300',
    button: 'bg-white text-rose-950 hover:bg-rose-100',
    icon: TriangleAlert,
  },
};

const FALLBACK_TONE = TONE_STYLES.promo;

const renderSegments = (segments, accentClass) => segments.map((segment, index) => (
  <span key={`${segment.text}-${index}`} className={segment.tone === 'gold' ? `font-semibold ${accentClass}` : 'text-white/90'}>
    {segment.text}
  </span>
));

function getDismissStorageKey(campaignId) {
  return `site-announcement:dismissed:${campaignId}`;
}

export default function SiteAnnouncementBar({
  config,
  pathname,
  onAction,
}) {
  const [isReady, setIsReady] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const tone = TONE_STYLES[config?.tone] || FALLBACK_TONE;
  const ToneIcon = tone.icon;

  const isActive = useMemo(() => (
    Boolean(
      config?.enabled
      && supportsCampaignPlacement(pathname, config?.placements)
      && isWithinCampaignWindow(config?.startAt, config?.endAt)
    )
  ), [config, pathname]);

  useEffect(() => {
    setIsReady(true);

    if (!config?.dismissible || !config?.campaignId) {
      setIsDismissed(false);
      return;
    }

    try {
      const rawValue = window.localStorage.getItem(getDismissStorageKey(config.campaignId));
      if (!rawValue) {
        setIsDismissed(false);
        return;
      }

      const expiresAt = Number(rawValue);
      if (!expiresAt || Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
        window.localStorage.removeItem(getDismissStorageKey(config.campaignId));
        setIsDismissed(false);
        return;
      }

      setIsDismissed(true);
    } catch (error) {
      console.error('Failed to restore announcement dismissal state:', error);
      setIsDismissed(false);
    }
  }, [config]);

  if (!isReady || !isActive || isDismissed) {
    return null;
  }

  const handleDismiss = (event) => {
    event.stopPropagation();

    if (config?.dismissible && config?.campaignId) {
      try {
        const ttlHours = Number(config?.dismissTtlHours || 24);
        const expiresAt = Date.now() + ttlHours * 60 * 60 * 1000;
        window.localStorage.setItem(
          getDismissStorageKey(config.campaignId),
          String(expiresAt)
        );
      } catch (error) {
        console.error('Failed to persist announcement dismissal state:', error);
      }
    }

    setIsDismissed(true);
  };

  return (
    <div className={`group relative w-full overflow-hidden px-4 py-2.5 transition-colors ${tone.shell}`}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.06),transparent_58%)] opacity-80" />

      <button
        type="button"
        onClick={onAction}
        className="relative mx-auto flex w-full max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-2 pr-10 text-center"
      >
        {Array.isArray(config?.leftSegments) && Array.isArray(config?.rightSegments) ? (
          <>
            <span className="text-sm">{renderSegments(config.leftSegments, tone.accent)}</span>
            <span className="hidden h-4 w-px bg-white/25 lg:block" />
            <span className="hidden text-sm md:block">{renderSegments(config.rightSegments, tone.accent)}</span>
          </>
        ) : (
          <>
        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${tone.label}`}>
          <ToneIcon className="h-3 w-3" />
          <span className="text-white">{config?.labelPrimary || config?.label}</span>
          {config?.labelDivider ? (
            <span className="text-white/70">{config.labelDivider}</span>
          ) : null}
          {(config?.labelAccent || config?.accent) ? (
            <span className={tone.accent}>{config?.labelAccent || config?.accent}</span>
          ) : null}
        </span>

        <span className="hidden h-4 w-px bg-white/15 md:block" />

        <span className="hidden text-sm md:block">
          <span className="text-white/82">{config?.messagePrimary || config?.message}</span>
          {(config?.messageAccent || config?.accent) ? (
            <>
              {' '}
              <span className={`font-semibold ${tone.accent}`}>{config?.messageAccent || config?.accent}</span>
            </>
          ) : null}
        </span>
          </>
        )}

        {config?.ctaLabel ? (
          <span className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1 text-sm font-semibold transition-transform duration-200 group-hover:scale-[1.03] ${tone.button}`}>
            {config.ctaLabel}
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </button>

      {config?.dismissible ? (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/8 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
